import { randomUUID } from "node:crypto";
import fs from "node:fs/promises";
import path from "node:path";
import JSON5 from "json5";
import { ensureNoriDirectories, type NoriPaths } from "@mewbleh/nori-persistence";

export type SecretRef = { source: "env"; name: string } | { source: "file"; path: string };
export type ProviderConfig = { apiKey?: SecretRef; baseUrl?: string; oauthBaseUrl?: string; authMode?: "auto" | "api-key" | "oauth"; enabled?: boolean };
export type McpServerConfig = {
  enabled?: boolean;
  transport?: "stdio" | "streamable-http";
  command?: string;
  args?: string[];
  cwd?: string;
  env?: Record<string, string>;
  url?: string;
  headers?: Record<string, string | SecretRef>;
  requestTimeoutMs?: number;
  toolFilter?: { include?: string[]; exclude?: string[] };
};
export type HookEntryConfig = { enabled?: boolean; paths?: string[]; messages?: number };
export type ChannelAccountConfig = {
  enabled?: boolean;
  token?: SecretRef;
  dmPolicy?: "pairing" | "allowlist" | "open" | "disabled";
  allowFrom?: string[];
  groupPolicy?: "allowlist" | "open" | "disabled";
  groups?: Record<string, { requireMention?: boolean }>;
  agentId?: string;
  [key: string]: unknown;
};

export type NoriConfig = {
  gateway: {
    mode: "local";
    bind: string;
    port: number;
    auth: { mode: "token"; token?: SecretRef };
  };
  agents: {
    defaults: {
      model: { primary: string; fallbacks: string[] };
      workspace: string;
    };
    list: Array<{ id: string; workspace?: string; model?: string }>;
  };
  session: { dmScope: "main" | "per-channel-peer" | "per-account-channel-peer" | "per-peer" };
  providers: Record<string, ProviderConfig>;
  channels: Record<string, ChannelAccountConfig | { accounts: Record<string, ChannelAccountConfig> }>;
  plugins: { allow: string[] };
  mcp: { servers: Record<string, McpServerConfig> };
  hooks: { internal: { enabled: boolean; entries: Record<string, HookEntryConfig> } };
  tools: {
    profile: "messaging" | "full";
    filesystem: boolean;
    shell: boolean;
    browser: boolean;
    automation: boolean;
    externalMessaging: boolean;
  };
};

export function defaultConfig(paths: NoriPaths): NoriConfig {
  return {
    gateway: { mode: "local", bind: "127.0.0.1", port: 18_789, auth: { mode: "token" } },
    agents: {
      defaults: { model: { primary: "", fallbacks: [] }, workspace: paths.workspaceDir },
      list: [{ id: "main", workspace: paths.workspaceDir }],
    },
    session: { dmScope: "per-channel-peer" },
    providers: {},
    channels: {},
    plugins: { allow: [] },
    mcp: { servers: {} },
    hooks: { internal: { enabled: false, entries: {} } },
    tools: {
      profile: "messaging",
      filesystem: false,
      shell: false,
      browser: false,
      automation: false,
      externalMessaging: false,
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function loopbackHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname.endsWith(".localhost");
}

function validateEndpoint(value: unknown, location: string, errors: string[], requireSecure: boolean): void {
  if (value === undefined) return;
  if (typeof value !== "string" || !value) { errors.push(`${location} must be a URL`); return; }
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") errors.push(`${location} must use HTTP or HTTPS`);
    if (url.username || url.password) errors.push(`${location} must not contain credentials`);
    if (url.hash) errors.push(`${location} must not contain a URL fragment`);
    if (requireSecure && url.protocol !== "https:" && !(url.protocol === "http:" && loopbackHostname(url.hostname))) errors.push(`${location} must use HTTPS unless it is loopback`);
  } catch {
    errors.push(`${location} must be a valid URL`);
  }
}

function merge<T>(base: T, update: unknown): T {
  if (!isRecord(base) || !isRecord(update)) return (update ?? base) as T;
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(update)) {
    result[key] = isRecord(value) && isRecord(result[key]) ? merge(result[key], value) : value;
  }
  return result as T;
}

export function mergeConfig<T>(base: T, update: unknown): T {
  return merge(base, update);
}

export function validateConfig(config: NoriConfig): string[] {
  const errors: string[] = [];
  if (config.gateway.mode !== "local") errors.push("gateway.mode must be 'local'");
  if (!Number.isInteger(config.gateway.port) || config.gateway.port < 1 || config.gateway.port > 65_535) {
    errors.push("gateway.port must be an integer from 1 to 65535");
  }
  if (!config.gateway.bind) errors.push("gateway.bind is required");
  if (!config.agents.defaults.workspace) errors.push("agents.defaults.workspace is required");
  if (!config.agents.defaults.model || typeof config.agents.defaults.model !== "object") errors.push("agents.defaults.model must be an object");
  if (!Array.isArray(config.agents.defaults.model?.fallbacks)) errors.push("agents.defaults.model.fallbacks must be an array");
  for (const agent of config.agents.list) {
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/u.test(agent.id)) errors.push(`invalid agent id: ${agent.id}`);
  }
  const validateSecret = (value: unknown, location: string) => {
    if (value === undefined) return;
    if (!isRecord(value) || (value.source !== "env" && value.source !== "file")) {
      errors.push(`${location} must be an env or file secret reference`);
      return;
    }
    if (value.source === "env" && (typeof value.name !== "string" || !value.name)) errors.push(`${location}.name is required`);
    if (value.source === "file" && (typeof value.path !== "string" || !value.path)) errors.push(`${location}.path is required`);
  };
  validateSecret(config.gateway.auth.token, "gateway.auth.token");
  for (const [id, provider] of Object.entries(config.providers)) {
    validateSecret(provider.apiKey, `providers.${id}.apiKey`);
    validateEndpoint(provider.baseUrl, `providers.${id}.baseUrl`, errors, false);
    validateEndpoint(provider.oauthBaseUrl, `providers.${id}.oauthBaseUrl`, errors, true);
    if (provider.authMode && !["auto", "api-key", "oauth"].includes(provider.authMode)) errors.push(`providers.${id}.authMode is invalid`);
  }
  for (const [id, channel] of Object.entries(config.channels)) {
    const configuredAccounts = (channel as { accounts?: unknown }).accounts;
    const accounts: Record<string, ChannelAccountConfig> = isRecord(configuredAccounts)
      ? configuredAccounts as Record<string, ChannelAccountConfig>
      : { default: channel as ChannelAccountConfig };
    for (const [accountId, account] of Object.entries(accounts)) validateSecret(account.token, `channels.${id}.${accountId}.token`);
  }
  if (!Array.isArray(config.plugins.allow) || config.plugins.allow.some((entry) => typeof entry !== "string")) errors.push("plugins.allow must be an array of plugin ids");
  if (!config.mcp || !isRecord(config.mcp.servers)) errors.push("mcp.servers must be an object");
  else for (const [name, server] of Object.entries(config.mcp.servers)) {
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/iu.test(name)) errors.push(`invalid MCP server name: ${name}`);
    const transport = server.transport ?? (server.url ? "streamable-http" : "stdio");
    if (transport === "stdio" && !server.command) errors.push(`mcp.servers.${name}.command is required for stdio`);
    if (transport === "streamable-http" && !server.url) errors.push(`mcp.servers.${name}.url is required for streamable-http`);
    if (server.url) validateEndpoint(server.url, `mcp.servers.${name}.url`, errors, true);
    if (server.args && (!Array.isArray(server.args) || server.args.some((entry) => typeof entry !== "string"))) errors.push(`mcp.servers.${name}.args must be an array of strings`);
    if (server.requestTimeoutMs !== undefined && (!Number.isInteger(server.requestTimeoutMs) || server.requestTimeoutMs < 100 || server.requestTimeoutMs > 300_000)) errors.push(`mcp.servers.${name}.requestTimeoutMs must be 100..300000`);
    for (const [header, value] of Object.entries(server.headers ?? {})) {
      if (typeof value !== "string") validateSecret(value, `mcp.servers.${name}.headers.${header}`);
      else if (/(?:authorization|api[_-]?key|token|secret|password)/iu.test(header)) errors.push(`mcp.servers.${name}.headers.${header} must use an env or file secret reference`);
    }
    for (const key of Object.keys(server.env ?? {})) {
      if (/(?:api[_-]?key|token|secret|password)/iu.test(key)) errors.push(`mcp.servers.${name}.env.${key} must not persist a secret; inherit it from the Nori process environment instead`);
    }
  }
  if (!config.hooks || !isRecord(config.hooks.internal) || !isRecord(config.hooks.internal.entries)) errors.push("hooks.internal.entries must be an object");
  else for (const [name, entry] of Object.entries(config.hooks.internal.entries)) {
    if (entry.paths && (!Array.isArray(entry.paths) || entry.paths.some((value) => typeof value !== "string"))) errors.push(`hooks.internal.entries.${name}.paths must be an array of strings`);
    if (entry.messages !== undefined && (!Number.isInteger(entry.messages) || entry.messages < 1 || entry.messages > 200)) errors.push(`hooks.internal.entries.${name}.messages must be 1..200`);
  }
  for (const key of ["filesystem", "shell", "browser", "automation", "externalMessaging"] as const) {
    if (typeof config.tools[key] !== "boolean") errors.push(`tools.${key} must be boolean`);
  }
  return errors;
}

export async function loadConfig(paths: NoriPaths, env: NodeJS.ProcessEnv = process.env): Promise<NoriConfig> {
  const defaults = defaultConfig(paths);
  let fileConfig: unknown = {};
  try {
    fileConfig = JSON5.parse(await fs.readFile(paths.configPath, "utf8"));
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
  }
  const config = merge(defaults, fileConfig);
  const port = env.NORI_GATEWAY_PORT ?? env.OPENCLAW_GATEWAY_PORT;
  if (port) config.gateway.port = Number.parseInt(port, 10);
  const bind = env.NORI_GATEWAY_BIND ?? env.OPENCLAW_GATEWAY_BIND;
  if (bind) config.gateway.bind = bind;
  const model = env.NORI_MODEL;
  if (model) config.agents.defaults.model.primary = model;
  const errors = validateConfig(config);
  if (errors.length) throw new Error(`Invalid Nori configuration:\n- ${errors.join("\n- ")}`);
  if (config.gateway.bind !== "127.0.0.1" && config.gateway.bind !== "::1" && !config.gateway.auth.token && !env.NORI_GATEWAY_TOKEN && !env.OPENCLAW_GATEWAY_TOKEN) {
    throw new Error("Non-loopback gateway binding requires gateway authentication");
  }
  return config;
}

export async function writeConfig(paths: NoriPaths, config: NoriConfig): Promise<void> {
  const errors = validateConfig(config);
  if (errors.length) throw new Error(`Invalid Nori configuration:\n- ${errors.join("\n- ")}`);
  await ensureNoriDirectories(paths);
  const temporaryPath = path.join(path.dirname(paths.configPath), `.${path.basename(paths.configPath)}.${process.pid}.${randomUUID()}.tmp`);
  const serialized = `${JSON.stringify(config, null, 2)}\n`;
  if (Buffer.byteLength(serialized) > 4 * 1024 * 1024) throw new Error("Nori configuration exceeds 4 MiB");
  const handle = await fs.open(temporaryPath, "wx", 0o600);
  let writeError: unknown;
  try {
    await handle.writeFile(serialized, "utf8");
    await handle.sync();
  } catch (error) {
    writeError = error;
  } finally {
    await handle.close();
  }
  if (writeError) {
    await fs.rm(temporaryPath, { force: true });
    throw writeError;
  }
  try {
    await fs.rename(temporaryPath, paths.configPath);
    if (process.platform !== "win32") await fs.chmod(paths.configPath, 0o600);
    try {
      const directory = await fs.open(path.dirname(paths.configPath), "r");
      try { await directory.sync(); }
      finally { await directory.close(); }
    } catch (error) {
      if (!(["EINVAL", "ENOTSUP", "EISDIR"] as Array<string | undefined>).includes((error as NodeJS.ErrnoException).code)) throw error;
    }
  } catch (error) {
    await fs.rm(temporaryPath, { force: true });
    throw error;
  }
}

export async function resolveSecret(ref: SecretRef | undefined, env: NodeJS.ProcessEnv = process.env): Promise<string | undefined> {
  if (!ref) return undefined;
  if (ref.source === "env") return env[ref.name];
  const resolved = path.resolve(ref.path.replace(/^~(?=$|\/)/u, env.HOME ?? ""));
  return (await fs.readFile(resolved, "utf8")).trim();
}
